import { Fragment, useEffect, useRef, useState } from 'react';
import { ArrowSortDown, ArrowSortUp, SortArrowSecondaryDown, SortArrowSecondaryUp } from '@sas/react-svg';
import cx from 'classnames';
import { useIsRTL } from '../../../locale';
import { renderDesignTokens } from '../../../theme';
import { ContextMenu } from '../../ContextMenu';
import { Heading } from '../../Heading';
import { BaseHelpPopup } from '../../HelpPopup/__internal__/';
import { Icon } from '../../Icon';
import { TableRegions } from '../TableRegions';
import { hasSelectionColumn, useDeltaDrag } from './internal';
import cn from '../Table.css.json';
import '../Table.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getSortIcon = column => {
  const sortIcons = {
    ascending: {
      primary: ArrowSortUp,
      secondary: SortArrowSecondaryUp
    },
    descending: {
      primary: ArrowSortDown,
      secondary: SortArrowSecondaryDown
    }
  };
  const {
    sortLevel,
    sortDirection
  } = column;
  const arrowType = sortLevel && sortLevel > 1 ? 'secondary' : 'primary';
  if (sortDirection && sortDirection !== 'data' && Object.keys(sortIcons).includes(sortDirection)) {
    return sortIcons[sortDirection][arrowType];
  }
  return sortIcons.ascending.primary;
};
const getSortIconToolTip = (column, totalSortedColumns, localize) => {
  const messageParts = [];
  if (column.sortDirection === 'ascending') {
    messageParts.push(localize('sortedColumnAsc.msg'));
  } else if (column.sortDirection === 'descending') {
    messageParts.push(localize('sortedColumnDesc.msg'));
  }
  if (column.sortLevel && column.sortLevel > 0 && totalSortedColumns > 1) {
    messageParts.push(localize('sortedColumnLevel.msg', {
      sortLevel: column.sortLevel.toString(),
      totalSortedColumns: totalSortedColumns.toString()
    }));
  }
  return messageParts.join(', ');
};
export const HeaderCellContent = props => {
  const {
    startDrag
  } = useDeltaDrag({
    onChange({
      deltaX
    }) {
      props.onDrag(deltaX);
    },
    onCommit: props.onDragEnd
  });
  const isRTL = useIsRTL();
  const {
    column,
    itemData
  } = props;
  const newHelpRef = useRef();
  const headerAccessoryContainer = useRef(null);
  const [contentPaddingInlineStart, setContentPaddingInlineStart] = useState();
  const helpRef = button => {
    newHelpRef.current = button;
    itemData.updateHelpRefs(column.key, itemData.getRowIdentifier(-1), newHelpRef);
    return newHelpRef;
  };
  const {
    sortDirection
  } = column;
  const showSortIcon = sortDirection && sortDirection !== 'data';
  const totalSortedColumns = itemData.columns.reduce((acc, currentColumn) => {
    return currentColumn.sortLevel && currentColumn.sortLevel > acc ? currentColumn.sortLevel : acc;
  }, 0);

  // Header accessories are part of the normal document flow, but when the cell alignment is 'center', the label needs to maintain true centering
  // when the accessories are present. To do this we measure the accessory container and offset that width with additional padding at the beginning
  // of the cell.
  useEffect(() => {
    if (!headerAccessoryContainer.current || column.alignment !== 'center' || !showSortIcon && !column.helpProps) {
      setContentPaddingInlineStart(undefined);
      return;
    }
    const width = headerAccessoryContainer.current.offsetWidth;
    setContentPaddingInlineStart(width);
  }, [column.alignment, column.helpProps, showSortIcon, setContentPaddingInlineStart]);
  if (!column) {
    return /*#__PURE__*/_jsx(Fragment, {});
  }
  let justify = 'flex-start';
  switch (column.alignment) {
    case 'center':
      justify = 'center';
      break;
    case 'end':
      justify = 'flex-end';
      break;
    case 'right':
      justify = isRTL ? 'flex-start' : 'flex-end';
      break;
    case 'left':
      justify = isRTL ? 'flex-end' : 'flex-start';
      break;
  }

  // header cell content will be derived from the custom headerCellContent function if given, otherwise the label or an empty string
  const cellContent = typeof column.headerCellContent === 'function' ? column.headerCellContent(props.columnIndex, itemData.disabled, itemData.readOnly) : /*#__PURE__*/_jsx(Heading, {
    className: cn.heading,
    wrapText: false,
    level: 5,
    icon: column.icon ? {
      icon: column.icon
    } : undefined,
    style: {
      justifyContent: justify
    },
    disabled: itemData.disabled,
    children: column.label
  }) || '';
  let dragContent = '';
  if (column.resizable !== false) {
    dragContent = /*#__PURE__*/_jsx("div", {
      className: !itemData.disabled ? cn.resizeIcon : undefined,
      role: "separator",
      "data-testid": `separator-${props.columnIndex}`,
      onClick: event => {
        event.stopPropagation();
      },
      onMouseDown: event => {
        // 0 is left mouse button
        if (event.button !== 0) return;
        startDrag([event.clientX, event.clientY]);
        props.onDragStart();
        // don't start a real browser drag and drop
        event.preventDefault();
      },
      onMouseUp: event => {
        // 0 is left mouse button
        if (event.button !== 0) return;
        event.preventDefault();
      }
    });
  }
  const style = renderDesignTokens({
    'pd-inline-start-adjustment': contentPaddingInlineStart ? `${contentPaddingInlineStart}px` : undefined
  }, 'table-cell');
  return /*#__PURE__*/_jsx(ContextMenu, {
    onContextMenu: e => {
      if (!e) return;
      const actualColumnIndex = itemData.columns.findIndex(c => c.key === column.key);
      const contextMenuInfo = {
        column,
        contextInfo: {
          position: {
            x: e.pageX,
            y: e.pageY
          },
          cell: {
            column: column.key
          }
        },
        position: {
          region: TableRegions.ColumnHeader,
          rowIndex: -1,
          colIndex: actualColumnIndex
        }
      };
      const doesHaveSelectionColumn = hasSelectionColumn(itemData.selectionMode, itemData.columns.length, itemData.rowCount, itemData.hideSelectionControlColumn);
      const headerContextMenu = itemData.headerContextMenu(column, actualColumnIndex, doesHaveSelectionColumn);
      itemData.updateContextMenuItems(headerContextMenu);
      if (headerContextMenu?.sections && headerContextMenu.sections.length > 0) {
        itemData.setContextMenuOpen(true);
      }
      itemData.storePreviousFocusIndicator(itemData.focusIndicator);
      itemData.setContextMenuInfo(contextMenuInfo);
    },
    disabled: itemData.disabled,
    children: /*#__PURE__*/_jsxs("div", {
      className: cx(cn.gridCellContentContainer, itemData.showContextArea(undefined, column.key) ? cn.contextArea : undefined),
      "data-context-menu-visible": itemData.showContextArea(undefined, column.key),
      style: {
        position: 'relative',
        textAlign: column.alignment ?? 'start',
        ...style,
        ...props.style
      },
      onMouseEnter: e => {
        props.itemData.onCellMouseEnter?.(e, -1, column.key);
      },
      onMouseDown: e => {
        itemData.setSortColumn(column.key);
        if (e.shiftKey) e.preventDefault();

        // update context menu info in case user activates context menu with keyboard
        const actualColumnIndex = itemData.columns.findIndex(c => c.key === column.key);
        const contextMenuInfo = {
          column,
          contextInfo: {
            position: {
              x: e.pageX,
              y: e.pageY
            },
            cell: {
              column: column.key
            }
          },
          position: {
            region: TableRegions.ColumnHeader,
            rowIndex: -1,
            colIndex: actualColumnIndex
          }
        };
        itemData.setContextMenuInfo(contextMenuInfo);
      },
      onMouseUp: e => {
        if (!itemData.disabled && e.button === 0 && column.sortable && itemData.sortRef.current === column.key) props.sort(e);
        itemData.setSortColumn('');
      },
      onFocus: event => {
        props.itemData.onCellFocus?.(event, -1, column.key);
      },
      children: [/*#__PURE__*/_jsxs("span", {
        className: cn.gridCellContent,
        style: {
          overflow: 'hidden'
        },
        children: [cellContent, column.helpProps || showSortIcon ? /*#__PURE__*/_jsxs("div", {
          className: cn.headerAccessoryContainer,
          ref: headerAccessoryContainer,
          children: [column.helpProps ? /*#__PURE__*/_jsxs(Fragment, {
            children: [/*#__PURE__*/_jsx(BaseHelpPopup, {
              ...column.helpProps,
              className: cx(cn.headerAccessory, cn.headerAccessoryHelpPopup),
              buttonRef: helpRef,
              disabled: itemData.disabled
            }), /*#__PURE__*/_jsx("span", {
              title: itemData.localize('help.msg')
            })]
          }) : null, showSortIcon ? /*#__PURE__*/_jsx(Icon, {
            className: cx(cn.headerAccessory, cn.headerAccessorySort),
            icon: getSortIcon(column),
            tooltip: getSortIconToolTip(column, totalSortedColumns, itemData.localize),
            disabled: itemData.disabled,
            testId: `sortIcon-${sortDirection}${props.column.sortLevel && props.column.sortLevel > 1 ? '-secondary' : '-primary'}-${props.columnIndex}`
          }) : null]
        }) : null]
      }), dragContent, itemData.isColumnResizing(column.key) ? /*#__PURE__*/_jsx("div", {
        className: cn.resizeColumnInProgressIcon
      }) : null]
    })
  });
};
//# sourceMappingURL=HeaderCellContent.js.map